"use client";

import { useState, useEffect } from "react";
import { Search, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Input } from "@/components/ui/input";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";

/* -------------------------
   Desktop menu
------------------------- */
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-1">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-10 w-20 bg-muted/30 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList>
        {menuItems.map(item => <NavigationMenuItem key={item.id}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-xl font-medium text-black hover:text-secondary-dark hover:bg-secondary-light/50 hover:underline hover:decoration-2 hover:underline-offset-4 transition-all">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

/* -------------------------
   Mobile menu
------------------------- */
function MobileMenu({
  onNavigate
}: {
  onNavigate: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Cargando menú…</div>;
  }
  return <nav className="flex flex-col">
      {menuItems.map(item => <Link key={item.id} to={item.href || "#"} onClick={onNavigate} className="px-4 py-4 text-lg font-medium border-b border-border hover:bg-muted/20">
          {item.label}
        </Link>)}
    </nav>;
}

/* -------------------------
   Header
------------------------- */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header className={`sticky top-0 z-50 w-full bg-background border-b border-border transition-shadow ${scrolled ? "shadow-soft" : ""}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 gap-4">

          {/* Logo */}
          <Link to="/" className="flex-shrink-0 -ml-2">
            <img src="https://hopeful-ram-2.10web.cloud/wp-content/uploads/2025/10/Gemini_Generated_Image_tg5f50tg5f50tg5f.png" alt="ProMaquinas" className="h-16" />
          </Link>

          {/* Botón hamburguesa (mobile) */}
          <div className="md:hidden">
            <button aria-label="Abrir menú" className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Menú desktop */}
          <div className="hidden md:flex flex-[0.8] justify-center">
            <MenuProvider menu_id="3">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {/* Buscador desktop */}
          <div className="hidden md:block">
            <div className={`relative rounded-lg overflow-hidden bg-muted/10 hover:bg-muted/20 transition-all ${searchFocused ? "bg-background shadow-medium" : ""}`}>
              <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none ${searchFocused ? "text-primary" : "text-muted-foreground"}`} />
              <Input type="search" placeholder="Buscar…" className={`pl-10 pr-4 bg-transparent border-muted/30 ${searchFocused ? "w-80 shadow-md" : "w-56"}`} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)} />
            </div>
          </div>
        </div>
      </div>

      {/* Panel menú móvil */}
      {mobileOpen && <div className="md:hidden border-t border-border bg-background">
          <MenuProvider menu_id="3">
            <MobileMenu onNavigate={() => setMobileOpen(false)} />
          </MenuProvider>
        </div>}
    </header>;
}